<template>
  <div>
    <DataTable
      v-model:expandedRows="expandedRows"
      :value="scenarios"
      class="p-datatable-striped"
      dataKey="id"
    >
      <Column expander></Column>
      <!-- <Column field="title" header="Name" /> -->
      <Column header="Name">
        <template #body="slotProps">
          <a
            :href="linktoScenario(slotProps.data.sizing_id, slotProps.data.id)"
            class="hype"
          >
            {{ slotProps.data.title ? slotProps.data.title : "" }}
          </a>
        </template>
      </Column>
      <Column
        :field="
          scenario =>
            scenario.journal_deleted_at === null ? 'Active' : 'Deleted'
        "
        header="Status"
      />
      <Column
        :field="scenario => scenario.r_performance_evaluations.length"
        header="Performance Evaluations"
      />
      <Column field="author.display_name" header="Owner" />

      <template #expansion="slotProps">
        <Accordion>
          <AccordionTab header="Equipment">
            <Accordion>
              <AccordionTab header="Pumps">
                <ul>
                  <li
                    v-for="pump in slotProps.data.equipment.r_xref_pumps"
                    :key="pump.id"
                  >
                    {{ pump.r_pump.manufacturer }} /
                    {{ pump.r_pump.series_model }}
                  </li>
                </ul>
              </AccordionTab>
              <AccordionTab header="Intakes">
                <ul>
                  <li
                    v-for="intake in slotProps.data.equipment.r_xref_intakes"
                    :key="intake.id"
                  >
                    {{ intake.r_intake.manufacturer }} /
                    {{ intake.r_intake.intake_type }}
                  </li>
                </ul>
              </AccordionTab>
              <AccordionTab header="Seals">
                <ul>
                  <li
                    v-for="seal in slotProps.data.equipment.r_xref_seals"
                    :key="seal.id"
                  >
                    {{ seal.r_seal.manufacturer }} /
                    {{ seal.r_seal.configuration }} / OD
                    {{ seal.r_seal.outer_diameter_inches }} inches /
                    {{ seal.r_seal.length_inches }} inches
                  </li>
                </ul>
              </AccordionTab>
              <AccordionTab header="Cables">
                <ul>
                  <li
                    v-for="cable in slotProps.data.equipment.r_xref_cables"
                    :key="cable.id"
                  >
                    {{ cable.r_cable.manufacturer }} /
                    {{ cable.r_cable.gauge }} gauge / {{ cable.r_cable.kv }} kv
                    /
                    {{ cable.r_cable.construction }}
                  </li>
                </ul>
              </AccordionTab>
              <AccordionTab header="Motors">
                <ul>
                  <!-- `${item.manufacturer} / ${item.part_number} / ${item.nameplate_horsepower} / ${item.nameplate_voltage} / ${item.nameplate_current}`; -->
                  <li v-if="slotProps.data.equipment.r_motor">
                    {{ slotProps.data.equipment.r_motor.manufacturer }} /
                    {{ slotProps.data.equipment.r_motor.part_number }} /
                    {{ slotProps.data.equipment.r_motor.nameplate_horsepower }}
                    HP /
                    {{ slotProps.data.equipment.r_motor.nameplate_voltage }} V /
                    {{ slotProps.data.equipment.r_motor.nameplate_current }} A
                  </li>
                </ul>
              </AccordionTab>
              <AccordionTab header="Top Tubing">
                <ul>
                  <li v-if="slotProps.data.well_properties.r_top_tubing">
                    {{
                      slotProps.data.well_properties.r_top_tubing.metric_name
                    }}
                    {{
                      slotProps.data.well_properties.r_top_tubing.imperial_name
                    }}
                  </li>
                </ul>
              </AccordionTab>
              <AccordionTab header="Bottom Tubing">
                <ul>
                  <li v-if="slotProps.data.well_properties.r_bottom_tubing">
                    {{
                      slotProps.data.well_properties.r_bottom_tubing.metric_name
                    }}
                    {{
                      slotProps.data.well_properties.r_bottom_tubing
                        .imperial_name
                    }}
                  </li>
                </ul>
              </AccordionTab>
              <AccordionTab header="Top Casing">
                <ul>
                  <li v-if="slotProps.data.well_properties.r_top_casing">
                    {{
                      slotProps.data.well_properties.r_top_casing.metric_name
                    }}
                    {{
                      slotProps.data.well_properties.r_top_casing.imperial_name
                    }}
                  </li>
                </ul>
              </AccordionTab>
              <AccordionTab header="Bottom Casing">
                <ul>
                  <li v-if="slotProps.data.well_properties.r_bottom_casing">
                    {{
                      slotProps.data.well_properties.r_bottom_casing.metric_name
                    }}
                    {{
                      slotProps.data.well_properties.r_bottom_casing
                        .imperial_name
                    }}
                  </li>
                </ul>
              </AccordionTab>
            </Accordion>
          </AccordionTab>
          <AccordionTab
            v-if="slotProps.data.r_performance_evaluations.length"
            header="Performance Evaluation"
          >
            <Accordion>
              <AccordionTab
                v-for="evaluation in slotProps.data.r_performance_evaluations"
                :key="evaluation.id"
                :header="`${evaluation.title} ${evaluation.journal_deleted_at ? '- (Deleted)' : ''}`"
              >
                <p v-if="!evaluation.journal_deleted_at">
                  <strong>Link:</strong>
                  <a
                    :href="
                      linktoPerformanceEvaluation(
                        evaluation.r_scenario.sizing_id,
                        evaluation.r_scenario.id,
                        evaluation.id
                      )
                    "
                    class="hype"
                    >Link to Evaluation</a
                  >
                </p>

                <p><strong>Pump Hz:</strong> {{ evaluation.pump_hz }}</p>
                <p>
                  <strong>Water Rate (STB/day):</strong>
                  {{ evaluation.water_rate_std_barrel_day }}
                </p>
                <p>
                  <strong>Gas Rate (MSCF/day):</strong>
                  {{ evaluation.gas_rate_mscf_day }}
                </p>
                <p>
                  <strong>Oil Rate (STB/day):</strong>
                  {{ evaluation.oil_rate_std_barrel_day }}
                </p>
                <p>
                  <strong>Tubing Pressure (PSIA):</strong>
                  {{ evaluation.tubing_pressure_psia }}
                </p>
                <p>
                  <strong>Casing Pressure (PSIA):</strong>
                  {{ evaluation.casing_pressure_psia }}
                </p>
                <p>
                  <strong>Pump Intake Pressure (PSIA):</strong>
                  {{ evaluation.pump_intake_pressure_psia }}
                </p>
                <p>
                  <strong>Intake Override Gas Separation (%):</strong>
                  {{ evaluation.intake_override_gas_separation_percent }}
                </p>
                <p>
                  <strong>Intake Gas Separation Override Type:</strong>
                  {{ evaluation.intake_gas_separation_override_type }}
                </p>
                <p>
                  <strong>Natural Override Gas Separation (%):</strong>
                  {{ evaluation.natural_override_gas_separation_percent }}
                </p>
                <p>
                  <strong>Natural Gas Separation Override Type:</strong>
                  {{ evaluation.natural_gas_separation_override_type }}
                </p>
                <p>
                  <strong>Created At:</strong>
                  {{ formatDate(evaluation.journal_created_at) }}
                </p>
                <p>
                  <strong>Updated At:</strong>
                  {{ formatDate(evaluation.journal_updated_at) }}
                </p>
              </AccordionTab>
            </Accordion>
          </AccordionTab>
        </Accordion>
      </template>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import type { Scenarios } from "gql/api";
import { defineProps, ref } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";

const props = defineProps({
  scenarios: {
    type: Array as () => Scenarios[],
    required: true
  }
});

const expandedRows = ref([]);

const linktoScenario = (sizingsId: string, id: string) => {
  const base = window.location.origin;
  return `${base}/sizings/${sizingsId}/scenarios/${id}/well`;
};
const linktoPerformanceEvaluation = (
  sizingsId: string,
  scenarioId: string,
  evalId: string
) => {
  const base = window.location.origin;
  return `${base}/sizings/${sizingsId}/scenarios/${scenarioId}/perfeval/${evalId}`;
};

const formatDate = (rowData: string | number | Date) => {
  return new Date(rowData).toLocaleDateString();
};
</script>

<style lang="scss" scoped>
::v-deep .p-accordion-header-link {
  color: inherit;
  background-color: transparent;
}
.hype {
  color: var(--info-500) !important;
  &:visited {
    color: var(--info-500);
  }
}
</style>
